import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, Link } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { getSidebarItems } from './config/SidebarItems';
import { useModuleEntitlements } from '../../modules/module-management';
import ModuleGuard from '../ModuleGuard';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { getFavorites, getCachedFavorites, reconstructFavoriteIcon, FavoriteItem } from '../../utils/favorites';
import {
  applySidebarLayout,
  fetchSidebarLayout,
  getCachedSidebarLayout,
  type SidebarLayout,
} from '../../utils/sidebarLayout';

// Import all module configs
import { moduleConfig as clientsConfig } from '../../modules/clients/config';
import { moduleConfig as appointmentsConfig } from '../../modules/crm-appointments/config';
import { moduleConfig as callsConfig } from '../../modules/crm-calls/config';
import { moduleConfig as remindersConfig } from '../../modules/reminders/config';
import { moduleConfig as crmNotesConfig } from '../../modules/crm-notes/config';
import { moduleConfig as clientServicesConfig } from '../../modules/client-services/config';
import { moduleConfig as serviceRenewalRemindersConfig } from '../../modules/service-renewal-reminders/config';
import { moduleConfig as marketingConfig } from '../../modules/marketing/config';
import { moduleConfig as leadsConfig } from '../../modules/crm-leads/config';
import { moduleConfig as clientInvoicesConfig } from '../../modules/client-invoices/config';
import { moduleConfig as invoicePaymentsConfig } from '../../modules/client-invoice-payments/config';
import { moduleConfig as crmTasksConfig } from '../../modules/crm-tasks/config';
import { moduleConfig as supportTicketConfig } from '../../modules/crm-support-ticket/config';
import { moduleConfig as todosConfig } from '../../modules/crm-todos/config';
import { moduleConfig as supportAgreementsConfig } from '../../modules/crm-support-agreements/config';
import { moduleConfig as projectsConfig } from '../../modules/crm-projects/config';
import { moduleConfig as packagesConfig } from '../../modules/crm-packages/config';

// Parameters modules
import { moduleConfig as pCrmExpenseCategoryConfig } from '../../modules/parameters/p_crm_expense_category/config';
import { moduleConfig as pCrmExpenseSubcategoryConfig } from '../../modules/parameters/p_crm_expense_subcategory/config';
import { moduleConfig as serviceTypeConfig } from '../../modules/parameters/service_type/config';
import { moduleConfig as frequencyTypeConfig } from '../../modules/parameters/frequency_type/config';
import { moduleConfig as durationConfig } from '../../modules/parameters/duration/config';
import { moduleConfig as pCrmServiceCategoryConfig } from '../../modules/parameters/p_crm_service_category/config';
import { moduleConfig as taskTypeConfig } from '../../modules/parameters/task_type/config';
import { moduleConfig as taskCategoryConfig } from '../../modules/parameters/task_category/config';
import { moduleConfig as taskStatusConfig } from '../../modules/parameters/task_status/config';
import { moduleConfig as priorityConfig } from '../../modules/parameters/priority/config';
import { moduleConfig as ticketStatusConfig } from '../../modules/parameters/ticket_status/config';
import { moduleConfig as callTypeConfig } from '../../modules/parameters/call_type/config';
import { moduleConfig as reminderTypeConfig } from '../../modules/parameters/reminder_type/config';
import { moduleConfig as taxAuthorityConfig } from '../../modules/parameters/tax_authority/config';
import { moduleConfig as pCountryConfig } from '../../modules/parameters/p_country/config';
import { moduleConfig as appStatusConfig } from '../../modules/parameters/app_status/config';
import { moduleConfig as pCrmInvoiceCategoryConfig } from '../../modules/parameters/p_crm_invoice_category/config';
import { moduleConfig as pCrmInvoiceTypeConfig } from '../../modules/parameters/p_crm_invoice_type/config';
import { moduleConfig as tagsConfig } from '../../modules/parameters/tags/config';
import { moduleConfig as knowledgeBaseConfig } from '../../modules/knowledge-base/config';
import { moduleConfig as usersConfig } from '../../modules/users/config';
import { moduleConfig as activityLogsConfig } from '../../modules/activity-logs/config';
import { moduleConfig as workflowsConfig } from '../../modules/client-workflows/config';
import { moduleConfig as customFieldsConfig } from '../../modules/custom-fields/config';
import { moduleConfig as payeesConfig } from '../../modules/payees-manager/config';
import { moduleConfig as payablesConfig } from '../../modules/payables/config';
import { moduleConfig as recurringPayablesConfig } from '../../modules/recurring-payables/config';
import { moduleConfig as offersConfig } from '../../modules/offers/config';
import { moduleConfig as offerTemplatesConfig } from '../../modules/offer-templates/config';
import { moduleConfig as catalogItemsConfig } from '../../modules/catalog-items/config';
import { moduleConfig as configurationSettingsConfig } from '../../modules/configuration-settings';
import { moduleConfig as suppliersConfig } from '../../modules/suppliers/config';
import { moduleConfig as supplierInvoicesConfig } from '../../modules/supplier-invoices/config';
import { moduleConfig as supplierInvoicePaymentsConfig } from '../../modules/supplier-invoice-payments/config';

// Deleted files (admin settings) - minimal config for breadcrumb
const filesStorageConfig = { module: { title: 'File storage' } };
const systemEmailTemplatesConfig = { module: { title: 'System email templates' } };
const emailNotificationSettingsConfig = { module: { title: 'Email notifications' } };
const payeesProjectionConfig = { module: { title: 'Payees Projection' } };
const preview11888Config = { module: { title: '11888 Preview' } };

// Route to module config mapping
const routeToConfigMap: Record<string, any> = {
  '/clients': clientsConfig,
  '/appointments': appointmentsConfig,
  '/calls': callsConfig,
  '/reminders': remindersConfig,
  '/crm_notes': crmNotesConfig,
  '/client-services': clientServicesConfig,
  '/service_renewal_reminders': serviceRenewalRemindersConfig,
  '/marketing': marketingConfig,
  '/leads': leadsConfig,
  '/client-invoices': clientInvoicesConfig,
  '/invoice-payments': invoicePaymentsConfig,
  '/payees-projection': payeesProjectionConfig,
  '/notifications': { module: { title: 'Notifications' } },
  '/crm_tasks': crmTasksConfig,
  '/support-ticket': supportTicketConfig,
  '/todos': todosConfig,
  '/support-agreements': supportAgreementsConfig,
  '/projects': projectsConfig,
  '/packages': packagesConfig,
  '/expense-categories': { module: { title: 'Expense Categories' } },
  '/payees': payeesConfig,
  '/payables': payablesConfig,
  '/recurring-payables': recurringPayablesConfig,
  '/suppliers': suppliersConfig,
  '/supplier-invoices': supplierInvoicesConfig,
  '/supplier-invoice-payments': supplierInvoicePaymentsConfig,
  '/offers': offersConfig,
  '/offer-templates': offerTemplatesConfig,
  '/catalog-items': catalogItemsConfig,
  '/configuration-settings': configurationSettingsConfig,
  '/p_crm_expense_category': pCrmExpenseCategoryConfig,
  '/p_crm_expense_subcategory': pCrmExpenseSubcategoryConfig,
  '/service_type': serviceTypeConfig,
  '/frequency_type': frequencyTypeConfig,
  '/duration': durationConfig,
  '/p_crm_service_category': pCrmServiceCategoryConfig,
  '/task_type': taskTypeConfig,
  '/task_category': taskCategoryConfig,
  '/task_status': taskStatusConfig,
  '/priority': priorityConfig,
  '/ticket_status': ticketStatusConfig,
  '/call_type': callTypeConfig,
  '/reminder_type': reminderTypeConfig,
  '/tax_authority': taxAuthorityConfig,
  '/p_country': pCountryConfig,
  '/app_status': appStatusConfig,
  '/p_crm_invoice_category': pCrmInvoiceCategoryConfig,
  '/p_crm_invoice_type': pCrmInvoiceTypeConfig,
  '/tags': tagsConfig,
  '/knowledge-base': knowledgeBaseConfig,
  '/admin/users': usersConfig,
  '/admin/settings/files-storage': filesStorageConfig,
  '/admin/settings/deleted-files': filesStorageConfig,
  '/admin/settings/system-email-templates': systemEmailTemplatesConfig,
  '/admin/settings/email-notifications': emailNotificationSettingsConfig,
  '/activity-logs': activityLogsConfig,
  '/workflows': workflowsConfig,
  '/custom-fields': customFieldsConfig,
  '/tools/11888-preview': preview11888Config,
};

// Report titles mapping
const reportTitles: Record<string, string> = {
  '/reports/ReportGenerator': 'Report Generator',
  '/reports/InvoicesByYear': 'Invoices by year',
  '/reports/InvoicesPaymentAnalysis': 'Payments',
  '/reports/ExpensesByYear': 'Expenses by Year Report',
  '/reports/Unpaid': 'Unpaid Clients Report',
  '/reports/AppointmentsReport': 'Appointments Report',
  '/reports/CallsReport': 'Calls Report',
  '/reports/MissedCalls': 'Missed Calls Report',
  '/reports/MonthlyServices': 'Services',
  '/reports/ClientTasksMonthly': 'Tasks',
  '/reports/TodosMonthlyReport': 'Todos',
};

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isCompact = false;
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const { appConfig } = useSettings();
  const sidebarStyle = appConfig.layout.sidebarStyle;
  const isTopMenu = sidebarStyle === 'topmenu';
  
  const { user, can, permissionsLoaded } = useAuth();
  const userRole = user?.role;
  const { entitlements } = useModuleEntitlements();
  const canView = React.useMemo(
    () => (permissionsLoaded ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded]
  );
  const sidebarItems = React.useMemo(
    () => getSidebarItems(userRole, entitlements, canView),
    [userRole, entitlements, canView]
  );

  const [sidebarLayout, setSidebarLayout] = React.useState<SidebarLayout | null>(() => getCachedSidebarLayout());
  React.useEffect(() => {
    let cancelled = false;
    fetchSidebarLayout().then((layout) => {
      if (!cancelled) setSidebarLayout(layout);
    });
    const onUpdated = () => {
      setSidebarLayout(getCachedSidebarLayout());
      fetchSidebarLayout().then((layout) => {
        if (!cancelled) setSidebarLayout(layout);
      });
    };
    window.addEventListener('sidebarLayoutUpdated', onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('sidebarLayoutUpdated', onUpdated);
    };
  }, []);

  const layoutSidebarItems = React.useMemo(
    () => applySidebarLayout(sidebarItems, sidebarLayout),
    [sidebarItems, sidebarLayout]
  );

  const topMenuSidebarItems = React.useMemo(
    () => layoutSidebarItems.filter((item) => item.name !== 'Favorites'),
    [layoutSidebarItems]
  );

  // Favorites for top menu layout (first group). groupKey used for separators between modules.
  type FavoriteEntry = { label: string; path: string; icon?: React.ReactNode; groupKey: string };

  const [favoriteEntries, setFavoriteEntries] = React.useState<FavoriteEntry[]>(() => {
    const cached = getCachedFavorites();
    if (!cached || !Array.isArray(cached)) return [];
    return cached.map((fav: FavoriteItem) => ({
      label: fav.title,
      path: fav.to,
      icon: reconstructFavoriteIcon(fav, sidebarItems),
      groupKey: fav.fullPath && fav.fullPath.trim() ? fav.fullPath.split(' > ')[0].trim() : 'Favorites',
    }));
  });

  React.useEffect(() => {
    const loadFavorites = async () => {
      try {
        const favs = await getFavorites();
        setFavoriteEntries(
          (Array.isArray(favs) ? favs : []).map((fav: FavoriteItem) => ({
            label: fav.title,
            path: fav.to,
            icon: reconstructFavoriteIcon(fav, sidebarItems),
            groupKey: fav.fullPath && fav.fullPath.trim() ? fav.fullPath.split(' > ')[0].trim() : 'Favorites',
          }))
        );
      } catch (error) {
        console.error('Error loading favorites for top menu:', error);
      }
    };

    loadFavorites();

    const handleFavoritesUpdated = () => {
      loadFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdated);
    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdated);
    };
  }, [sidebarItems]);

  // Recursive function to find icon in nested menu items
  const findIconInMenuItems = (items: typeof sidebarItems, path: string): React.ReactNode | undefined => {
    for (const item of items) {
      // Check if this item matches
      if (item.path) {
        if (item.path === '/' && path === '/') {
          return item.icon;
        }
        if (item.path !== '/' && path.startsWith(item.path)) {
          return item.icon;
        }
      }
      
      // Check views for path match
      if (item.views) {
        const matchingView = item.views.find(view => {
          if (view.path === '/' && path === '/') return true;
          if (view.path !== '/' && path.startsWith(view.path)) return true;
          return false;
        });
        if (matchingView) {
          return item.icon;
        }
      }
      
      // Recursively check children
      if (item.children) {
        const childIcon = findIconInMenuItems(item.children, path);
        if (childIcon) {
          // Return parent icon if child doesn't have one, otherwise return child icon
          return childIcon || item.icon;
        }
        // If path matches a child path, return parent icon
        const hasMatchingChild = item.children.some(child => {
          if (child.path) {
            if (child.path === '/' && path === '/') return true;
            if (child.path !== '/' && path.startsWith(child.path)) return true;
          }
          // Check child views
          if (child.views) {
            return child.views.some(view => {
              if (view.path === '/' && path === '/') return true;
              if (view.path !== '/' && path.startsWith(view.path)) return true;
              return false;
            });
          }
          return false;
        });
        if (hasMatchingChild) {
          return item.icon;
        }
      }
    }
    return undefined;
  };

  // Get icon for current route
  const getIconForPath = (path: string): React.ReactNode | undefined => {
    // Custom icons for specific routes
    if (path === '/profile-settings') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    }
    
    if (path === '/settings') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    }
    
    if (path === '/client-vault' || path.startsWith('/client-vault')) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    }
    
    return findIconInMenuItems(sidebarItems, path);
  };
  
  // Map routes to titles
  const getPageInfo = (): { title?: string; subtitle?: string } => {
    const path = location.pathname;
    
    // Dashboard
    if (path === '/' || path === '/dashboard') {
      return { title: 'Dashboard' };
    }
    // Settings pages
    if (path === '/settings') {
      return { title: 'Application Settings' };
    }

    if (path === '/settings/offer-pdf-layouts') {
      return { title: 'Offer PDF layouts' };
    }
    
    if (path === '/profile-settings') {
      return { title: 'Settings' };
    }

    if (path === '/import/invoice' || path.startsWith('/import/invoice/')) {
      return { title: 'Import Invoices' };
    }

    // Reports
    if (path.startsWith('/reports/')) {
      const reportTitle = reportTitles[path];
      if (reportTitle) {
        return { title: reportTitle };
      }
      return { title: 'Reports' };
    }

    // Calendar view
    if (path === '/appointments_calendar_view') {
      return { title: 'Calendar' };
    }

    // Todos Calendar View
    if (path === '/todos_monthly_view') {
      return {
        title: 'Todos',
      };
    }

    // Notes Cardboard View
    if (path === '/cardboard-crm-notes') {
      return {
        title: 'Notes Cardboard View',
      };
    }

    // Client Vault
    if (path === '/vault-templates') {
      return {
        title: 'Vault Templates',
        subtitle: 'Manage folder templates for client vaults',
      };
    }

    if (path === '/presets-management') {
      return {
        title: 'Presets Management',
      };
    }
    
    if (path === '/client-vault' || path.startsWith('/client-vault')) {
      return { 
        title: 'Client Vault',
        subtitle: 'Securely manage client information, credentials, and files'
      };
    }


    // Services (client services yearly view)
    if (path === '/client-services-yearly') {
      return {
        title: 'Services',
      };
    }

    // Monthly Tasks
    if (path === '/crm_tasks-monthly') {
      return {
        title: 'Monthly Tasks',
      };
    }

    // Report Generator
    if (path === '/report-generator') {
      return {
        title: 'Report Generator',
      };
    }

    // Tools
    if (path === '/tools/database-browser') {
      return { title: 'Database Browser' };
    }

    if (path.match(/^\/clients\/\d+\/summary$/)) {
      return { title: 'Client Summary' };
    }

    // Find matching module config (sort by length descending to match most specific routes first)
    const sortedRoutes = Object.entries(routeToConfigMap).sort((a, b) => b[0].length - a[0].length);
    
    for (const [routePrefix, config] of sortedRoutes) {
      if (path.startsWith(routePrefix)) {
        // Check for new form
        if (path === `${routePrefix}/new` || (path.startsWith(routePrefix) && path.endsWith('/new'))) {
          const formTitle = config?.form?.new?.title;
          const moduleTitle = config?.module?.title || 'Item';
          return {
            title: formTitle || `New ${moduleTitle}`,
          };
        }
        // Check for edit form
        if (path.startsWith(routePrefix) && path.includes('/edit')) {
          const formTitle = config?.form?.edit?.title;
          const moduleTitle = config?.module?.title || 'Item';
          return {
            title: formTitle || `Edit ${moduleTitle}`,
          };
        }
        // Check for detail view (numeric ID after route prefix)
        if (path.match(new RegExp(`^${routePrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/\\d+$`))) {
          return {
            title: config?.module?.title || 'Details',
          };
        }
        // Default to module title for list view
        if (path === routePrefix || path.startsWith(`${routePrefix}/`)) {
          return {
            title: config?.module?.title || 'List',
          };
        }
      }
    }
    
    return {
      title: undefined,
    };
  };

  const { title, subtitle } = getPageInfo();
  const icon = getIconForPath(location.pathname);

  const { colors } = useTheme();
  const isAppointmentsCalendar = location.pathname === '/appointments_calendar_view';
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const isCompose = false;

  // True when current route is this menu entry (exact) or a child of it (e.g. /todos/5 for entry /todos).
  // Avoids marking both "List View" and "Monthly View" active when path is e.g. /todos_monthly_view.
  const isEntryPathActive = (pathname: string, entryPath: string) =>
    pathname === entryPath || (entryPath !== '/' && pathname.startsWith(entryPath + '/'));

  // Collect flat menu entries for a sidebar item (used in top menu style).
  // Labels: "Module – View" when multiple views (e.g. "Tasks – List View"); otherwise module name.
  // groupKey is used to render separators between modules.
  type MenuEntry = { label: string; path: string; icon?: React.ReactNode; groupKey: string };
  const collectMenuEntries = (item: any): MenuEntry[] => {
    const entries: MenuEntry[] = [];
    const moduleName = item.name ?? '';

    if (Array.isArray(item.views) && item.views.length > 0) {
      for (const view of item.views) {
        if (view.path) {
          const label =
            item.views.length > 1 ? `${item.name} – ${(view as any).label ?? view.path}` : item.name;
          entries.push({
            label,
            path: view.path,
            icon: (view as any).icon ?? item.icon,
            groupKey: moduleName,
          });
        }
      }
    } else if (item.path) {
      entries.push({
        label: item.name ?? item.path,
        path: item.path,
        icon: item.icon,
        groupKey: moduleName,
      });
    }

    if (Array.isArray(item.children)) {
      for (const child of item.children) {
        entries.push(...collectMenuEntries(child));
      }
    }

    return entries;
  };

  // Open dropdown index for top menu groups (topmenu style)
  // Use -1 as the special index for Favorites group
  const [openTopMenuIndex, setOpenTopMenuIndex] = React.useState<number | null>(null);
  const topMenuButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const [topMenuDropdownPosition, setTopMenuDropdownPosition] = React.useState<{ top: number; left: number } | null>(null);

  const HOVER_OPEN_DELAY_MS = 1000;
  const CLOSE_DELAY_MS = 150;
  const openDelayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeDelayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseInDropdownRef = React.useRef(false);
  const topMenuDropdownRef = React.useRef<HTMLDivElement | null>(null);
  // Track when a menu was opened explicitly by click so it doesn't auto-close on small mouse movements
  const openByClickIndexRef = React.useRef<number | null>(null);

  const clearOpenDelay = () => {
    if (openDelayRef.current) {
      clearTimeout(openDelayRef.current);
      openDelayRef.current = null;
    }
  };
  const clearCloseDelay = () => {
    if (closeDelayRef.current) {
      clearTimeout(closeDelayRef.current);
      closeDelayRef.current = null;
    }
  };

  const scheduleOpen = (index: number) => {
    clearCloseDelay();
    clearOpenDelay();
    openDelayRef.current = setTimeout(() => {
      openDelayRef.current = null;
      // Hover-open: not pinned by click
      openByClickIndexRef.current = null;
      setOpenTopMenuIndex(index);
    }, HOVER_OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    clearOpenDelay();
    // Don't auto-close a menu that was explicitly opened by click
    if (openByClickIndexRef.current !== null && openByClickIndexRef.current === openTopMenuIndex) {
      return;
    }
    if (mouseInDropdownRef.current) return;
    clearCloseDelay();
    closeDelayRef.current = setTimeout(() => {
      closeDelayRef.current = null;
      setOpenTopMenuIndex(null);
    }, CLOSE_DELAY_MS);
  };

  const openImmediately = (index: number) => {
    clearOpenDelay();
    clearCloseDelay();
    setOpenTopMenuIndex((prev) => {
      if (prev === index) {
        openByClickIndexRef.current = null;
        return null;
      }
      openByClickIndexRef.current = index;
      return index;
    });
  };

  React.useEffect(() => {
    return () => {
      clearOpenDelay();
      clearCloseDelay();
    };
  }, []);

  // Position the top-menu dropdown in a portal; measure trigger button when open
  React.useEffect(() => {
    if (openTopMenuIndex === null) {
      setTopMenuDropdownPosition(null);
      return;
    }
    const el = topMenuButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTopMenuDropdownPosition({ top: rect.bottom + 4, left: rect.left });
  }, [openTopMenuIndex]);

  return (
    <div className={`flex h-screen ${colors.background}`}>
      {!isTopMenu && (
        <Sidebar isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />
      )}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 transition-all duration-300',
          !isTopMenu && 'overflow-hidden'
        )}
      >
        <Topbar
          title={title}
          subtitle={subtitle}
          icon={icon}
          isMobileMenuOpen={isMobileOpen}
          onToggleMobileMenu={() => setIsMobileOpen(!isMobileOpen)}
        />
        {isTopMenu && (
          <nav
            className={cn(
              'flex items-center border-b min-h-[44px]',
              'bg-slate-50/95 dark:bg-slate-800/60 backdrop-blur-sm',
              colors.border
            )}
            aria-label="Main navigation"
          >
            <div className="flex-1 flex items-center gap-1 px-2 sm:px-3 overflow-x-auto scrollbar-thin">
              {/* Favorites as first group when using top menu layout */}
              {favoriteEntries.length > 0 && (
                <div className="relative flex-shrink-0">
                  <button
                    ref={openTopMenuIndex === -1 ? topMenuButtonRef : undefined}
                    type="button"
                    onMouseEnter={() => scheduleOpen(-1)}
                    onMouseLeave={scheduleClose}
                    onClick={() => openImmediately(-1)}
                    className={cn(
                      'px-3 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 transition-all duration-150',
                      'hover:bg-slate-200/70 dark:hover:bg-slate-700/60',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-teal-500',
                      openTopMenuIndex === -1
                        ? 'focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-800'
                        : '',
                      favoriteEntries.some((e) => isEntryPathActive(location.pathname, e.path))
                        ? 'text-teal-700 dark:text-teal-400'
                        : colors.textSecondary,
                      openTopMenuIndex === -1
                        ? 'bg-slate-200/80 dark:bg-slate-700/70 ring-1 ring-slate-300/50 dark:ring-slate-600/50'
                        : ''
                    )}
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.377 2.454a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.538 1.118l-3.378-2.454a1 1 0 00-1.176 0l-3.378 2.454c-.783.57-1.838-.196-1.538-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.002 9.393c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.286-3.966z" />
                      </svg>
                    </span>
                    <span>Favorites</span>
                    <svg
                      className={cn('w-4 h-4 transition-transform', openTopMenuIndex === -1 && 'rotate-180')}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}

              {topMenuSidebarItems.map((item, index) => {
                const entries = collectMenuEntries(item);
                if (!entries.length) return null;

                const isActiveGroup = entries.some((entry) =>
                  isEntryPathActive(location.pathname, entry.path)
                );
                const isOpen = openTopMenuIndex === index;
                const groupLabel = item.name || `Group ${index + 1}`;

                return (
                  <div key={`${groupLabel}-${index}`} className="relative flex-shrink-0">
                    <button
                      ref={isOpen ? topMenuButtonRef : undefined}
                      type="button"
                      onMouseEnter={() => scheduleOpen(index)}
                      onMouseLeave={scheduleClose}
                      onClick={() => openImmediately(index)}
                      className={cn(
                        'px-3 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 transition-all duration-150',
                        'hover:bg-slate-200/70 dark:hover:bg-slate-700/60',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-teal-500 focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-800',
                        isActiveGroup && !isOpen && 'text-teal-700 dark:text-teal-400',
                        isOpen && 'bg-slate-200/80 dark:bg-slate-700/70 ring-1 ring-slate-300/50 dark:ring-slate-600/50',
                        !isActiveGroup && !isOpen && colors.textSecondary
                      )}
                    >
                      {item.icon && (
                        <span className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
                          {item.icon}
                        </span>
                      )}
                      <span>{groupLabel}</span>
                      <svg
                        className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </nav>
        )}
        {isTopMenu && openTopMenuIndex !== null && (
          <div
            className="fixed inset-0"
            style={zStyle(Z_INDEX.topMenuBackdrop)}
            aria-hidden
            onClick={() => {
              clearOpenDelay();
              clearCloseDelay();
              openByClickIndexRef.current = null;
              setOpenTopMenuIndex(null);
            }}
          />
        )}
        {isTopMenu &&
          openTopMenuIndex !== null &&
          topMenuDropdownPosition !== null &&
          (() => {
            type DropdownEntry = { label: string; path: string; icon?: React.ReactNode; groupKey: string };
            let entries: DropdownEntry[] = [];

            if (openTopMenuIndex === -1) {
              entries = favoriteEntries;
            } else {
              const item = topMenuSidebarItems[openTopMenuIndex];
              entries = item ? collectMenuEntries(item) : [];
            }

            if (!entries.length) return null;

            return createPortal(
              <div
                ref={topMenuDropdownRef}
                className={cn(
                  'fixed min-w-[240px] max-w-[320px] max-h-[min(28rem,72vh)] overflow-y-auto',
                  'rounded-xl shadow-xl border py-2',
                  'animate-in fade-in slide-in-from-top-1 duration-200',
                  colors.surface,
                  colors.border
                )}
                style={{
                  top: topMenuDropdownPosition.top,
                  left: topMenuDropdownPosition.left,
                  ...zStyle(Z_INDEX.topMenuDropdown),
                }}
                role="menu"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => {
                  clearCloseDelay();
                  mouseInDropdownRef.current = true;
                }}
                onMouseLeave={() => {
                  mouseInDropdownRef.current = false;
                  clearCloseDelay();
                  closeDelayRef.current = setTimeout(() => {
                    closeDelayRef.current = null;
                    // Respect click-pinned menus: only auto-close hover-open menus
                    if (openByClickIndexRef.current === null) {
                      setOpenTopMenuIndex(null);
                    }
                  }, CLOSE_DELAY_MS);
                }}
                onFocus={() => clearCloseDelay()}
                onBlur={(e) => {
                  if (!topMenuDropdownRef.current?.contains(e.relatedTarget as Node)) {
                    clearCloseDelay();
                    closeDelayRef.current = setTimeout(() => {
                      closeDelayRef.current = null;
                      if (openByClickIndexRef.current === null) {
                        setOpenTopMenuIndex(null);
                      }
                    }, CLOSE_DELAY_MS);
                  }
                }}
              >
                {entries.map((entry, idx) => {
                  const isNewSection = idx === 0 || entry.groupKey !== entries[idx - 1].groupKey;
                  return (
                    <React.Fragment key={`${entry.path}-${entry.label}-${idx}`}>
                      {isNewSection && idx > 0 && (
                        <div className={cn('my-2 border-t', colors.border)} role="separator" />
                      )}
                      {isNewSection && (
                        <div
                          className={cn(
                            'px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider',
                            idx > 0 ? 'pt-3' : 'pt-0',
                            colors.textMuted
                          )}
                          role="presentation"
                        >
                          {entry.groupKey}
                        </div>
                      )}
                      <Link
                        to={entry.path}
                        role="menuitem"
                        onClick={() => {
                          clearOpenDelay();
                          clearCloseDelay();
                          openByClickIndexRef.current = null;
                          setOpenTopMenuIndex(null);
                        }}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg mx-1.5 transition-colors',
                          'hover:bg-slate-100 dark:hover:bg-slate-700/60',
                          isEntryPathActive(location.pathname, entry.path)
                            ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300'
                            : colors.textSecondary
                        )}
                      >
                        {entry.icon && (
                          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-md bg-slate-100 dark:bg-slate-700/50 [&>svg]:w-4 [&>svg]:h-4">
                            {entry.icon}
                          </span>
                        )}
                        <span className="truncate">{entry.label}</span>
                      </Link>
                    </React.Fragment>
                  );
                })}
              </div>,
              document.body
            );
          })()}
        <main
          className={cn(
            'flex-1 overflow-hidden min-w-0 flex flex-col',
            colors.background,
            isAppointmentsCalendar ? 'p-0' : isCompose ? 'px-4 sm:px-6 pt-0 pb-4 sm:pb-5' : isDashboard ? 'px-4 sm:px-6 pt-4 sm:pt-5 pb-4 sm:pb-5 w-full' : isCompact ? 'p-2 sm:p-4' : 'pt-1 px-4 pb-6'
          )}
        >
          <div className={cn(
            'flex flex-col min-w-0',
            isAppointmentsCalendar && 'h-full min-h-0',
            isDashboard && 'h-full min-h-0 w-full',
            isCompose && 'h-full min-h-0',
            !isAppointmentsCalendar && !isDashboard && !isCompose && 'h-full min-h-0 overflow-y-auto'
          )}>
            <ErrorBoundary
              fallback={
                <div className="flex flex-col items-center justify-center flex-1 min-h-[320px] p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong on this page.</p>
                  <Link to="/dashboard" className="text-teal-700 dark:text-teal-400 hover:underline">Back to Dashboard</Link>
                </div>
              }
            >
              <ModuleGuard>{children}</ModuleGuard>
            </ErrorBoundary>
          </div>
        </main>
        {appConfig.features.enableFooter && <Footer />}
      </div>
    </div>
  );
};

export default MainLayout;
